"use client";
import {
  __async
} from "../chunk-M66XENHI.js";
import { setupWorker } from "../workers/workerWrapper.js";
import { distributeNewShare, waitUntilTrue } from "@getpara/core-sdk";
import * as uuid from "uuid";
function isKeygenComplete(ctx, userId, walletId) {
  return __async(this, null, function* () {
    const wallets = yield ctx.client.getWallets(userId);
    const wallet = wallets.data.wallets.find((w) => w.id === walletId);
    return !!wallet.address;
  });
}
function isRefreshComplete(ctx, userId, walletId, partnerId, protocolId) {
  return __async(this, null, function* () {
    const { isDone } = yield ctx.client.isRefreshDone(userId, walletId, partnerId, protocolId);
    return isDone;
  });
}
function isPreKeygenComplete(ctx, pregenIdentifier, pregenIdentifierType, walletId) {
  return __async(this, null, function* () {
    const wallets = yield ctx.client.getPregenWallets({ [pregenIdentifierType]: [pregenIdentifier] });
    const wallet = wallets.wallets.find((w) => w.id === walletId);
    return !!wallet.address;
  });
}
function keygen(ctx, userId, type, secretKey, skipDistribute = false, sessionCookie, emailProps = {}) {
  return new Promise((resolve, reject) => __async(this, null, function* () {
    const workId = uuid.v4();
    let worker = null;
    worker = yield setupWorker(
      ctx,
      (res) => __async(this, null, function* () {
        yield waitUntilTrue(() => __async(this, null, function* () {
          return isKeygenComplete(ctx, userId, res.walletId);
        }), 15e3, 1e3);
        if (skipDistribute) {
          resolve({
            signer: res.signer,
            walletId: res.walletId,
            recoveryShare: null
          });
          return;
        }
        const recoveryShare = yield distributeNewShare({
          ctx,
          userId,
          walletId: res.walletId,
          userShare: res.signer,
          emailProps
        });
        resolve({
          signer: res.signer,
          walletId: res.walletId,
          recoveryShare
        });
      }),
      (error) => {
        reject(error);
      },
      workId
    );
    worker.postMessage({
      env: ctx.env,
      apiKey: ctx.apiKey,
      cosmosPrefix: ctx.cosmosPrefix,
      params: { userId, secretKey, type },
      functionType: "KEYGEN",
      offloadMPCComputationURL: ctx.offloadMPCComputationURL,
      disableWorkers: ctx.disableWorkers,
      sessionCookie,
      useDKLS: ctx.useDKLS,
      disableWebSockets: ctx.disableWebSockets,
      wasmOverride: ctx.wasmOverride,
      workId
    });
  }));
}
function preKeygen(ctx, pregenIdentifier, pregenIdentifierType, type, secretKey, _skipDistribute = false, partnerId, sessionCookie) {
  return new Promise((resolve, reject) => __async(this, null, function* () {
    const workId = uuid.v4();
    let worker = null;
    worker = yield setupWorker(
      ctx,
      (res) => __async(this, null, function* () {
        yield waitUntilTrue(
          () => __async(this, null, function* () {
            return isPreKeygenComplete(ctx, pregenIdentifier, pregenIdentifierType, res.walletId);
          }),
          15e3,
          1e3
        );
        resolve({
          signer: res.signer,
          walletId: res.walletId,
          recoveryShare: null
        });
      }),
      (error) => {
        reject(error);
      },
      workId
    );
    const email = void 0;
    const params = { pregenIdentifier, pregenIdentifierType, type, secretKey, partnerId, email };
    if (pregenIdentifierType === "EMAIL") {
      params.email = pregenIdentifier;
    }
    worker.postMessage({
      env: ctx.env,
      apiKey: ctx.apiKey,
      cosmosPrefix: ctx.cosmosPrefix,
      params,
      functionType: "PREKEYGEN",
      offloadMPCComputationURL: ctx.offloadMPCComputationURL,
      disableWorkers: ctx.disableWorkers,
      sessionCookie,
      useDKLS: ctx.useDKLS,
      disableWebSockets: ctx.disableWebSockets,
      wasmOverride: ctx.wasmOverride,
      workId
    });
  }));
}
function refresh(ctx, sessionCookie, userId, walletId, share, oldPartnerId, newPartnerId, keyShareProtocolId) {
  return new Promise((resolve, reject) => __async(this, null, function* () {
    const workId = uuid.v4();
    let worker = null;
    worker = yield setupWorker(
      ctx,
      (res) => __async(this, null, function* () {
        if (!(yield waitUntilTrue(() => __async(this, null, function* () {
          return isRefreshComplete(ctx, userId, walletId, newPartnerId);
        }), 15e3, 1e3))) {
          reject(new Error("refresh failed"));
          return;
        }
        const { protocolId, signer } = res;
        resolve({
          signer,
          protocolId
        });
      }),
      (error) => {
        reject(error);
      },
      workId
    );
    worker.postMessage({
      env: ctx.env,
      apiKey: ctx.apiKey,
      params: { userId, walletId, share, oldPartnerId, newPartnerId, keyShareProtocolId },
      functionType: "REFRESH",
      disableWorkers: ctx.disableWorkers,
      sessionCookie,
      useDKLS: ctx.useDKLS,
      disableWebSockets: ctx.disableWebSockets,
      wasmOverride: ctx.wasmOverride,
      returnObject: true,
      workId
    });
  }));
}
function ed25519Keygen(ctx, userId, sessionCookie, _emailProps = {}) {
  return new Promise((resolve, reject) => __async(this, null, function* () {
    const workId = uuid.v4();
    let worker = null;
    worker = yield setupWorker(
      ctx,
      (res) => __async(this, null, function* () {
        yield waitUntilTrue(() => __async(this, null, function* () {
          return isKeygenComplete(ctx, userId, res.walletId);
        }), 15e3, 1e3);
        resolve({
          signer: res.signer,
          walletId: res.walletId,
          recoveryShare: null
        });
      }),
      (error) => {
        reject(error);
      },
      workId
    );
    worker.postMessage({
      env: ctx.env,
      apiKey: ctx.apiKey,
      cosmosPrefix: ctx.cosmosPrefix,
      params: { userId },
      functionType: "ED25519_KEYGEN",
      disableWorkers: ctx.disableWorkers,
      sessionCookie,
      disableWebSockets: ctx.disableWebSockets,
      wasmOverride: ctx.wasmOverride,
      workId
    });
  }));
}
function ed25519PreKeygen(ctx, pregenIdentifier, pregenIdentifierType, sessionCookie) {
  return new Promise((resolve, reject) => __async(this, null, function* () {
    const workId = uuid.v4();
    let worker = null;
    worker = yield setupWorker(
      ctx,
      (res) => __async(this, null, function* () {
        yield waitUntilTrue(
          () => __async(this, null, function* () {
            return isPreKeygenComplete(ctx, pregenIdentifier, pregenIdentifierType, res.walletId);
          }),
          15e3,
          1e3
        );
        resolve({
          signer: res.signer,
          walletId: res.walletId,
          recoveryShare: null
        });
      }),
      (error) => {
        reject(error);
      },
      workId
    );
    const email = void 0;
    const params = { pregenIdentifier, pregenIdentifierType, email };
    if (pregenIdentifierType === "EMAIL") {
      params.email = pregenIdentifier;
    }
    worker.postMessage({
      env: ctx.env,
      apiKey: ctx.apiKey,
      cosmosPrefix: ctx.cosmosPrefix,
      params,
      functionType: "ED25519_PREKEYGEN",
      disableWorkers: ctx.disableWorkers,
      sessionCookie,
      disableWebSockets: ctx.disableWebSockets,
      wasmOverride: ctx.wasmOverride,
      workId
    });
  }));
}
export {
  ed25519Keygen,
  ed25519PreKeygen,
  keygen,
  preKeygen,
  refresh
};
